import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  approveSurveyResponse,
  bulkApproveSurveyResponses,
  bulkRejectSurveyResponses,
  rejectSurveyResponse,
} from '../supabaseService';
import { fetchSurveyResponses } from '../utils/fetchSurveyResponses';
import { formatAnswersLines } from '../utils/surveyResponseHelpers';
import { formatSurveyorLabel, syncSurveyorsFromServer } from '../utils/surveySurveyorRegistry';

const PAGE_SIZE = 25;
const selectClass = 'rounded-lg border border-de-gold/25 bg-[#0c1a30] px-3 py-2 text-sm text-white';
const inputClass = 'w-full rounded-lg border border-de-gold/25 bg-[#0c1a30] px-3 py-2 text-sm text-white';

const FRAUD_RED = new Set(['self_survey']);
const FRAUD_YELLOW = new Set(['cross_freelancer_duplicate', 'sequence_pattern']);

function parseFraudFlags(row) {
  const raw = row?.fraud_flags;
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch (_err) {
      return [];
    }
  }
  return [];
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function PaymentBadge({ status, t }) {
  const map = {
    pending: 'bg-amber-950/50 text-amber-200 border-amber-400/30',
    approved: 'bg-emerald-950/50 text-emerald-300 border-emerald-400/30',
    rejected: 'bg-red-950/50 text-red-300 border-red-400/30',
  };
  const label = {
    pending: t('surveyReviewStatusPending'),
    approved: t('surveyReviewStatusApproved'),
    rejected: t('surveyReviewStatusRejected'),
  };
  return (
    <Badge className={map[status] || 'bg-white/10 text-white'}>
      {label[status] || status || '—'}
    </Badge>
  );
}

function FraudFlags({ flags, t }) {
  if (!flags.length) return <span className="text-de-muted">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {flags.map((flag) => {
        const isRed = FRAUD_RED.has(flag);
        const labelKey = `surveyFraud_${flag}`;
        const label = t(labelKey) !== labelKey ? t(labelKey) : flag;
        return (
          <Badge
            key={flag}
            className={isRed
              ? 'border-red-400/50 bg-red-950/60 text-red-200'
              : 'border-amber-400/40 bg-amber-950/40 text-amber-200'}
          >
            {isRed ? '⛔ ' : '⚠ '}
            {label}
          </Badge>
        );
      })}
    </div>
  );
}

export default function SurveyReviewTab({ onPendingCountChange = null }) {
  const { t } = useLanguage();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionId, setActionId] = useState('');
  const [selected, setSelected] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [page, setPage] = useState(0);
  const [paymentFilter, setPaymentFilter] = useState('pending');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectTargetIds, setRejectTargetIds] = useState([]);
  const [rejectReason, setRejectReason] = useState('Invalid/Fake Number');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await syncSurveyorsFromServer();
    } catch (syncErr) {
      // Non-critical — continue loading
      console.warn('Surveyors sync failed:', syncErr);
    }
    try {
      const data = await fetchSurveyResponses();
      const reviewable = (data || []).filter((r) => !r.is_legacy);
      setRows(reviewable);
      const pending = reviewable.filter(
        (r) => r.payment_status === 'pending' && !r.payout_locked,
      ).length;
      if (onPendingCountChange) onPendingCountChange(pending);
    } catch (err) {
      const isAuthErr = err?.message === 'auth_required'
        || err?.message?.includes('auth');
      setError(isAuthErr
        ? 'Session expired. Please login again.'
        : (err?.message || t('surveyLoadFailed')));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [t, onPendingCountChange]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    let list = rows;
    if (paymentFilter !== 'all') {
      list = list.filter((r) => r.payment_status === paymentFilter);
    }
    return [...list].sort(
      (a, b) => new Date(b.submitted_at) - new Date(a.submitted_at),
    );
  }, [rows, paymentFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const selectedIds = useMemo(
    () => Object.keys(selected).filter((id) => selected[id]),
    [selected],
  );
  const pageSelectableIds = pageRows
    .filter((r) => r.payment_status === 'pending' && !r.payout_locked)
    .map((r) => r.id);

  useEffect(() => {
    setPage(0);
  }, [paymentFilter]);

  useEffect(() => {
    if (page >= pageCount) setPage(Math.max(0, pageCount - 1));
  }, [page, pageCount]);

  const toggleSelect = (id) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleSelectPage = () => {
    const allSelected = pageSelectableIds.every((id) => selected[id]);
    setSelected((prev) => {
      const next = { ...prev };
      pageSelectableIds.forEach((id) => {
        next[id] = !allSelected;
      });
      return next;
    });
  };

  const handleApprove = async (id) => {
    setActionError('');
    setActionId(id);
    const { error: err } = await approveSurveyResponse(id);
    setActionId('');
    if (err) {
      setActionError(err);
      return;
    }
    await load();
    setSelected((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const openReject = (ids) => {
    setRejectTargetIds(ids);
    setRejectReason('Invalid/Fake Number');
    setRejectOpen(true);
  };

  const handleRejectConfirm = async () => {
    const reason = rejectReason.trim();
    if (!reason) return;
    setActionError('');
    setRejectOpen(false);
    setActionId('bulk');
    let err = '';
    if (rejectTargetIds.length === 1) {
      const result = await rejectSurveyResponse(rejectTargetIds[0], reason);
      err = result.error;
    } else {
      const result = await bulkRejectSurveyResponses(rejectTargetIds, reason);
      err = result.error;
    }
    setActionId('');
    if (err) {
      setActionError(err);
      return;
    }
    setSelected({});
    await load();
  };

  const handleBulkApprove = async () => {
    const ids = selectedIds.filter((id) => {
      const row = rows.find((r) => r.id === id);
      return row?.payment_status === 'pending' && !row?.payout_locked;
    });
    if (!ids.length) return;
    setActionError('');
    setActionId('bulk');
    const { error: err } = await bulkApproveSurveyResponses(ids);
    setActionId('');
    if (err) {
      setActionError(err);
      return;
    }
    setSelected({});
    await load();
  };

  if (loading) {
    return <p className="py-12 text-center text-de-muted">{t('surveyLoading')}</p>;
  }

  if (error) {
    return (
      <div className="space-y-3">
        <div className="rounded-xl border border-de-accent/40 bg-de-accent/10 p-4 text-de-accent">{error}</div>
        <Button type="button" variant="outline" className="border-de-gold/40 text-de-gold" onClick={load}>
          {t('surveyRetry')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <section className="rounded-xl border border-de-gold/20 bg-[#0c1a30]/80 p-4 text-sm text-de-muted">
        <p className="m-0 font-semibold text-de-gold">{t('surveyReviewIntroTitle')}</p>
        <p className="mt-2 m-0">{t('surveyReviewIntroHint')}</p>
      </section>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-de-muted">
          {t('surveyReviewFilterPayment')}
          <select
            className={selectClass}
            value={paymentFilter}
            onChange={(e) => setPaymentFilter(e.target.value)}
          >
            <option value="pending">{t('surveyReviewStatusPending')}</option>
            <option value="approved">{t('surveyReviewStatusApproved')}</option>
            <option value="rejected">{t('surveyReviewStatusRejected')}</option>
            <option value="all">{t('surveyAll')}</option>
          </select>
        </label>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            className="bg-emerald-600 text-white hover:bg-emerald-500"
            disabled={!selectedIds.length || actionId === 'bulk'}
            onClick={handleBulkApprove}
          >
            {t('surveyReviewBulkApprove')}
            {selectedIds.length ? ` (${selectedIds.length})` : ''}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="border-red-400/40 text-red-300"
            disabled={!selectedIds.length || actionId === 'bulk'}
            onClick={() => openReject(selectedIds)}
          >
            {t('surveyReviewBulkReject')}
            {selectedIds.length ? ` (${selectedIds.length})` : ''}
          </Button>
          <Button type="button" size="sm" variant="outline" className="border-de-gold/40 text-de-gold" onClick={load}>
            {t('surveyRetry')}
          </Button>
        </div>
      </div>

      {actionError && (
        <p className="m-0 text-sm text-de-accent">{actionError}</p>
      )}

      <div className="overflow-x-auto rounded-xl border border-de-gold/15 bg-[#0c1a30]/60">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <input
                  type="checkbox"
                  checked={pageSelectableIds.length > 0 && pageSelectableIds.every((id) => selected[id])}
                  disabled={!pageSelectableIds.length}
                  onChange={toggleSelectPage}
                  aria-label={t('surveyReviewSelectAll')}
                />
              </TableHead>
              <TableHead>{t('surveyColDate')}</TableHead>
              <TableHead>{t('surveyColSurveyor')}</TableHead>
              <TableHead>{t('surveyColName')}</TableHead>
              <TableHead>{t('surveyColPhone')}</TableHead>
              <TableHead>{t('surveyColVillage')}</TableHead>
              <TableHead>{t('surveyColType')}</TableHead>
              <TableHead>{t('surveyReviewColFlags')}</TableHead>
              <TableHead>{t('surveyReviewColPayment')}</TableHead>
              <TableHead className="text-right">{t('surveyQrFreelancerActions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRows.map((row) => {
              const flags = parseFraudFlags(row);
              const canReview = row.payment_status === 'pending' && !row.payout_locked;
              const expanded = expandedId === row.id;
              const busy = actionId === row.id;
              return (
                <React.Fragment key={row.id}>
                  <TableRow className={expanded ? 'bg-de-gold/5' : undefined}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={Boolean(selected[row.id])}
                        disabled={!canReview}
                        onChange={() => toggleSelect(row.id)}
                        aria-label={`Select ${row.respondent_name}`}
                      />
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs">{formatDate(row.submitted_at)}</TableCell>
                    <TableCell className="text-xs">
                      {row.surveyor ? formatSurveyorLabel(row.surveyor) : '—'}
                    </TableCell>
                    <TableCell>{row.respondent_name || '—'}</TableCell>
                    <TableCell className="whitespace-nowrap">{row.whatsapp_number || '—'}</TableCell>
                    <TableCell>{row.village || '—'}</TableCell>
                    <TableCell>{row.survey_type || '—'}</TableCell>
                    <TableCell><FraudFlags flags={flags} t={t} /></TableCell>
                    <TableCell>
                      <PaymentBadge status={row.payment_status} t={t} />
                      {row.rejection_reason && (
                        <p className="mt-1 m-0 max-w-[140px] text-[10px] text-de-muted">{row.rejection_reason}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {canReview ? (
                        <div className="flex flex-wrap justify-end gap-1">
                          <Button
                            type="button"
                            size="sm"
                            className="bg-emerald-600 text-white hover:bg-emerald-500"
                            disabled={busy}
                            onClick={() => handleApprove(row.id)}
                          >
                            {busy ? '…' : t('surveyReviewApprove')}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="border-red-400/40 text-red-300"
                            disabled={busy}
                            onClick={() => openReject([row.id])}
                          >
                            {t('surveyReviewReject')}
                          </Button>
                        </div>
                      ) : row.payout_locked ? (
                        <Badge className="bg-slate-800 text-slate-300">{t('surveyReviewLocked')}</Badge>
                      ) : (
                        <span className="text-xs text-de-muted">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={10} className="p-0">
                      <button
                        type="button"
                        className="w-full px-3 py-1 text-left text-[10px] text-de-muted hover:text-de-gold"
                        onClick={() => setExpandedId(expanded ? null : row.id)}
                      >
                        {expanded ? t('surveyReviewHideAnswers') : t('surveyReviewShowAnswers')}
                      </button>
                      {expanded && (
                        <pre className="m-0 overflow-x-auto whitespace-pre-wrap border-t border-de-gold/10 px-3 py-2 font-mono text-xs text-[#c8d0dc]">
                          {formatAnswersLines(row.answers, {
                            surveyType: row.survey_type,
                            lang: row.language || 'mr',
                          }).join('\n') || '—'}
                        </pre>
                      )}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {filtered.length === 0 && (
        <p className="py-8 text-center text-sm text-de-muted">{t('surveyReviewEmpty')}</p>
      )}

      {filtered.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="m-0 text-xs text-de-muted">
            {t('surveyShowing')
              .replace('{from}', String(page * PAGE_SIZE + 1))
              .replace('{to}', String(Math.min((page + 1) * PAGE_SIZE, filtered.length)))
              .replace('{total}', String(filtered.length))}
          </p>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" disabled={page <= 0} onClick={() => setPage((p) => p - 1)}>
              {t('surveyPrev')}
            </Button>
            <Button type="button" size="sm" variant="outline" disabled={page >= pageCount - 1} onClick={() => setPage((p) => p + 1)}>
              {t('surveyNextPage')}
            </Button>
          </div>
        </div>
      )}

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="border-de-gold/30 bg-[#0c1a30] text-white">
          <DialogHeader>
            <DialogTitle className="text-de-gold">{t('surveyReviewRejectTitle')}</DialogTitle>
            <DialogDescription className="text-de-muted">
              {t('surveyReviewRejectHint').replace('{n}', String(rejectTargetIds.length))}
            </DialogDescription>
          </DialogHeader>
          <label className="flex flex-col gap-1 text-xs text-de-muted">
            {t('surveyReviewRejectReason')}
            <input
              className={inputClass}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Invalid/Fake Number"
              maxLength={500}
            />
          </label>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" className="border-de-gold/30" onClick={() => setRejectOpen(false)}>
              {t('surveyReviewCancel')}
            </Button>
            <Button
              type="button"
              className="bg-red-700 text-white hover:bg-red-600"
              disabled={!rejectReason.trim()}
              onClick={handleRejectConfirm}
            >
              {t('surveyReviewConfirmReject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export { parseFraudFlags, FRAUD_RED, FRAUD_YELLOW };
